import { useLazyQuery } from '@apollo/client';
import { phoenixLegalHotelSuggestionQuery } from '@marriott/mi-legal-graphql';
import { InputTextField } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { Dropdown } from '../../molecules/Dropdown';
import {
  formatHotelFieldsWithRequiredLabels,
  processHotelSearchResultsWithRequiredFields,
} from '../../molecules/HotelBookInformation/helper';
import { PhoenixLegalHotelSuggestionQueryResult } from '../../molecules/HotelBookInformation/HotelBookInformation.types';
import { SingleDayPicker } from '../../molecules/SingleDayPicker';
import { TypeAndSearchWithLink } from '../../molecules/TypeAndSearchWithLink';
import { useAuthStore, useLegalGenericStore } from '../../store';
import { allowDigitsCommaDot, allowOnlyDigits } from '../../utils/inputSanitizers';
import { scrollToTop } from '../../utils/scrollUtils';
import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import type { BrgModelProps, FormData } from './BrgClaimForm.types';
// import mockHotelResponse from './__mock__/phoenixLegalHotelSuggestionQuery.json';

const GET_HOTELS_SEARCH_RESULTS_OPERATION_NAME = 'phoenixLegalHotelSuggestionQuery';

interface BrgReservationInfoProps {
  reservationInformation: BrgModelProps['reservationInformation'];
  dropDownPlaceholder: BrgModelProps['dropDownPlaceholder'];
  currencyOptions: Array<{ label: string; value: string }>;
  isCurrencyDataLoading: boolean;
  showAPIAlert: () => void;
}
export function BrgReservationInfo({
  dropDownPlaceholder,
  reservationInformation,
  isCurrencyDataLoading,
  currencyOptions,
  showAPIAlert,
}: BrgReservationInfoProps) {
  const {
    control,
    formState: { errors },
    resetField,
    getValues,
    watch,
  } = useFormContext<FormData>();

  const checkInDate = watch('checkin');
  const checkOutDate = watch('checkout');

  useEffect(() => {
    if (checkInDate >= checkOutDate) {
      resetField('checkout'); // Clear checkout if it's before check-in
    }
  }, [checkInDate, checkOutDate, resetField]);

  // Calendar configuration for check-in and check-out dates
  const calendarConfig = useMemo(() => {
    const today = moment().toDate();
    const oneYearLater = moment().add(1, 'year').toDate();
    const nextDayAfterCheckIn = moment(checkInDate).add(1, 'd').toDate();

    return {
      checkIn: {
        month: checkInDate || today,
        fromMonth: today,
        toMonth: moment().add(1, 'year').subtract(1, 'd').toDate(),
        disabledDays: {
          before: today,
          after: moment().add(1, 'year').subtract(1, 'd').toDate(),
        },
      },
      checkOut: {
        month: nextDayAfterCheckIn,
        fromMonth: checkInDate,
        toMonth: oneYearLater,
        disabledDays: {
          before: nextDayAfterCheckIn,
          after: oneYearLater,
        },
      },
    };
  }, [checkInDate]);

  const calenderLabels = useMemo(
    () => ({
      months: reservationInformation.months?.map(item => item.monthLabel),
      weekdaysLong: reservationInformation.weekDaysLong?.map(item => item.weekLongLabel),
      weekdaysShort: reservationInformation.weekDaysShort?.map(item => item.weekShortLabel),
    }),
    []
  );

  const { isAuthorMode, language } = useLegalGenericStore();
  const { sessionId } = useAuthStore();

  const [getHotelList, { data: hotelResponse }] = useLazyQuery<PhoenixLegalHotelSuggestionQueryResult>(
    phoenixLegalHotelSuggestionQuery,
    {
      context: generateApolloClientHeadersForUXLCalls(sessionId, GET_HOTELS_SEARCH_RESULTS_OPERATION_NAME, language),
      fetchPolicy: 'no-cache',
      onError: error => {
        console.error(`Something went wrong while process hotel search results: ${JSON.stringify(error)}`);
        showAPIAlert();
        scrollToTop();
      },
    }
  );

  const hotelSearchResults = useMemo(() => {
    const hotelName = getValues('hotelName');
    const hotelSearchNodes = hotelResponse?.suggestedPlacesBySearchString.edges || [];
    return hotelName?.length >= 3
      ? processHotelSearchResultsWithRequiredFields(hotelSearchNodes).map(formatHotelFieldsWithRequiredLabels)
      : [];
  }, [getValues, hotelResponse?.suggestedPlacesBySearchString.edges]);

  // Handles fetching hotel list based on input
  const handleHotelSearch = (query: string) => {
    if (!isAuthorMode && query.length >= 3) {
      getHotelList({
        variables: {
          search: { filters: { suggestionTypes: 'PROPERTY' }, query },
        },
      });
    }
  };

  // Debounce API calls to prevent excessive requests
  const debouncedHotelSearch = useDebouncedCallback(handleHotelSearch, 500);

  return (
    <div id="reservation-info" className="max-w-brg">
      <h2>{reservationInformation.componentTitle}</h2>
      <p>{reservationInformation.componentDescription}</p>
      <div className="m-form-group">
        <Controller
          name="confirmationNumber"
          control={control}
          rules={{ required: true, minLength: 5 }}
          render={({ field }) => {
            return (
              <InputTextField
                label={reservationInformation?.confirmationNumber?.label}
                inputValue={field.value}
                assignInputValue
                className="m-input-field"
                messageToShow={reservationInformation?.confirmationNumber?.errorLabel}
                showErrorMessage={!!errors.confirmationNumber}
                getInputValue={val => field.onChange(allowOnlyDigits(val))}
                inputMaxLength={9}
                getInputProps={() => ({ inputMode: 'numeric' })}
              />
            );
          }}
        />
        <Controller
          name="hotelName"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            const Label = reservationInformation?.typeAndSearchWithLink?.searchFieldLabel || {
              label: '',
              errorLabel: '',
            };

            return (
              <TypeAndSearchWithLink
                searchFieldLabel={Label}
                inputValue={field.value ?? ''}
                className="m-input-field brg-hotel-search"
                showErrorMsg={!!fieldState.error}
                testId="hotel_name"
                infoLabel="hotel_search_result"
                name="hotelName"
                getSearchValue={(query, callApi) => {
                  field.onChange(query);
                  if (callApi) debouncedHotelSearch(query);
                }}
                hotelSearchResults={hotelSearchResults}
                inputMaxLength={255}
                inputType="text"
                searchResultFirstFieldValue={
                  reservationInformation?.typeAndSearchWithLink?.searchResultFirstFieldValue ?? ''
                }
                searchResultLastFieldValue={
                  reservationInformation?.typeAndSearchWithLink?.searchResultLastFieldValue ?? ''
                }
              />
            );
          }}
        />
        <Controller
          name="checkin"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <SingleDayPicker
              placeholder={dropDownPlaceholder}
              label={reservationInformation?.checkInDate?.label ?? ''}
              defaultDate={field.value}
              errorMessage={errors.checkin ? reservationInformation?.checkInDate?.errorLabel : ''}
              dayPickerPros={{
                ...calenderLabels,
                fixedWeeks: true,
                ...calendarConfig.checkIn,
              }}
              onDayClick={field.onChange}
            />
          )}
        />
        <Controller
          name="checkout"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <SingleDayPicker
              placeholder={dropDownPlaceholder}
              label={reservationInformation?.checkOutDate?.label ?? ''}
              defaultDate={field.value}
              disabled={!checkInDate}
              errorMessage={errors.checkout ? reservationInformation?.checkOutDate?.errorLabel : ''}
              dayPickerPros={{
                ...calenderLabels,
                ...calendarConfig.checkOut,
              }}
              onDayClick={field.onChange}
            />
          )}
        />
        <Controller
          name="marriottRateReserved"
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <InputTextField
                label={reservationInformation?.rateReserved?.label}
                messageToShow={reservationInformation?.rateReserved?.errorLabel}
                showErrorMessage={!!errors.marriottRateReserved}
                assignInputValue
                inputValue={field.value}
                getInputValue={val => field.onChange(allowDigitsCommaDot(val))}
                inputMaxLength={16}
                className="m-input-field"
              />
            );
          }}
        />
        <Controller
          name="rateReservedCurrency"
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <Dropdown
                id={'rateReservedCurrency'}
                name={'rateReservedCurrency'}
                options={currencyOptions}
                placeholder={dropDownPlaceholder}
                label={reservationInformation?.currency?.label}
                errorMessage={errors.rateReservedCurrency ? reservationInformation?.currency?.errorLabel : ''}
                value={field.value}
                handleChange={field.onChange}
                className={clsx({
                  'skeleton-loader': isCurrencyDataLoading,
                })}
              />
            );
          }}
        />
      </div>
    </div>
  );
}
